import { useUser, useClerk, SignIn, SignUp } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import RightPanel from './components/RightPanel';
import BackgroundAnimation from './components/BackgroundAnimation';
import MeetingDetail from './components/MeetingDetail';
import NewMeetingModal from './components/NewMeetingModal';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import MeetingsPage from './pages/MeetingsPage';
import ActionsPage from './pages/ActionsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LivePage from './pages/LivePage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import ZoomPage from './pages/ZoomPage';
import PricingPage from './pages/PricingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPage from './pages/RefundPage';
import { useData, computeStats } from './hooks/useData';

const SHOW_RIGHT_PANEL = ['home','meetings','actions','analytics','live','settings','profile','zoom','meeting-detail','pricing'];

function AuthScreen({ mode, onBack }) {
  const appearance = {
    variables: {
      colorPrimary: '#00ff88', colorBackground: '#0d0f10',
      colorText: '#eceef0', colorTextSecondary: '#7c8490',
      colorInputBackground: '#141618', colorInputText: '#eceef0', borderRadius: '8px',
    },
    elements: {
      card: { background: '#0d0f10', border: '1px solid #1e2124', boxShadow: '0 40px 80px rgba(0,0,0,0.6)' },
      formButtonPrimary: { background: '#00ff88', color: '#000', fontWeight: 600 },
      footerActionLink: { color: '#00ff88' },
      socialButtonsBlockButton: { background: '#141618', border: '1px solid #2a2e33', color: '#eceef0' },
      socialButtonsBlockButtonText: { color: '#eceef0' },
    },
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'1.5rem', padding:'2rem', position:'relative' }}>
      <BackgroundAnimation />
      <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'1.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
          <div style={{ width:32, height:32, background:'var(--accent)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🎙</div>
          <span style={{ fontFamily:'var(--mono)', fontSize:16, fontWeight:600, color:'var(--text)' }}>Clarimeet</span>
        </div>
        {mode === 'signin'
          ? <SignIn appearance={appearance} routing="hash" afterSignInUrl="/" />
          : <SignUp appearance={appearance} routing="hash" afterSignUpUrl="/" />
        }
        <button onClick={onBack} style={{ background:'none', border:'none', color:'var(--text-3)', fontSize:13, cursor:'pointer', fontFamily:'var(--mono)' }}>
          ← Back to home
        </button>
      </div>
    </div>
  );
}

function Dashboard({ user }) {
  const { signOut } = useClerk();
  const [page, setPage] = useState('home');
  const [activeMeetingId, setActiveMeetingId] = useState(null);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [newMeetingMode, setNewMeetingMode] = useState('file');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const refresh = () => setRefreshTrigger(n => n + 1);

  useEffect(() => {
    if (user?.id) window.__clerk_user_id = user.id;
  }, [user?.id]);

  useEffect(() => { setSidebarOpen(false); }, [page]);

  // Handle phone/browser back button
  useEffect(() => {
    // Set initial history state
    window.history.replaceState({ page: 'home', id: null }, '', window.location.href);

    const handlePopState = (e) => {
      if (e.state?.page) {
        setPage(e.state.page);
        if (e.state.id) setActiveMeetingId(e.state.id);
      } else {
        setPage('home');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const { meetings, actions } = useData(refreshTrigger);
  const stats = computeStats(meetings, actions);

  const openNewMeeting = (mode = 'file') => { setNewMeetingMode(mode); setShowNewMeeting(true); };
  const handleMeetingCreated = (id) => { refresh(); setActiveMeetingId(id); setPage('meeting-detail'); window.history.pushState({ page: 'meeting-detail', id }, '', window.location.href); };

  const handleNavigate = (target, id = null) => {
    if (target === 'new-meeting') { openNewMeeting('file'); return; }
    if (target === 'meeting-detail' && id) setActiveMeetingId(id);
    setPage(target);
    setSidebarOpen(false);
    window.history.pushState({ page: target, id }, '', window.location.href);
  };

  const handleSelectMeeting = (id) => { setActiveMeetingId(id); setPage('meeting-detail'); setSidebarOpen(false); window.history.pushState({ page: 'meeting-detail', id }, '', window.location.href); };

  const renderPage = () => {
    switch (page) {
      case 'home':      return <HomePage onNavigate={handleNavigate} onNewMeeting={openNewMeeting} stats={stats} refreshTrigger={refreshTrigger} />;
      case 'meetings':  return <MeetingsPage onSelectMeeting={handleSelectMeeting} onNewMeeting={openNewMeeting} refreshTrigger={refreshTrigger} />;
      case 'actions':   return <ActionsPage onSelectMeeting={handleSelectMeeting} refreshTrigger={refreshTrigger} />;
      case 'analytics': return <AnalyticsPage refreshTrigger={refreshTrigger} />;
      case 'live':      return <LivePage onNavigate={handleNavigate} />;
      case 'settings':  return <SettingsPage />;
      case 'profile':   return <ProfilePage user={user} onNavigate={handleNavigate} onSignOut={() => signOut()} />;
      case 'zoom':      return <ZoomPage onMeetingImported={(id) => { refresh(); setActiveMeetingId(id); setPage('meeting-detail'); }} />;
      case 'pricing':   return <PricingPage onNavigate={handleNavigate} />;
      case 'terms':     return <TermsPage />;
      case 'privacy':   return <PrivacyPage />;
      case 'refund':    return <RefundPage />;
      case 'meeting-detail':
        return activeMeetingId
          ? <MeetingDetail key={activeMeetingId} meetingId={activeMeetingId} onDelete={() => { refresh(); setPage('meetings'); }} />
          : null;
      default: return null;
    }
  };

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', position:'relative', background:'transparent' }}>
      <BackgroundAnimation />

      {/* Sidebar overlay for mobile */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <div className={`sidebar-wrapper ${sidebarOpen ? 'open' : ''}`}>
        <Sidebar
          activePage={page}
          onNavigate={handleNavigate}
          activeMeetingId={activeMeetingId}
          onSelectMeeting={handleSelectMeeting}
          refreshTrigger={refreshTrigger}
          stats={stats}
          user={user}
          sidebarOpen={sidebarOpen}
        />
      </div>

      {/* Main area */}
      <div style={{ display:'flex', flexDirection:'column', flex:1, position:'relative', zIndex:1, overflow:'hidden' }}>

        {/* Mobile top bar */}
        <div className="mobile-topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>
            <span /><span /><span />
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:24, height:24, background:'var(--accent)', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, boxShadow:'0 0 10px rgba(0,255,136,0.4)' }}>🎙</div>
            <span style={{ fontFamily:'var(--mono)', fontSize:14, fontWeight:700, color:'var(--text)' }}>Clarimeet</span>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => openNewMeeting('file')} style={{ padding:'6px 12px', fontSize:12 }}>
            + New
          </button>
        </div>

        {/* Content row */}
        <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
          <main className="main">{renderPage()}</main>
          {SHOW_RIGHT_PANEL.includes(page) && (
            <div className="right-panel-mobile">
              <RightPanel refreshTrigger={refreshTrigger} />
            </div>
          )}
        </div>
      </div>

      {showNewMeeting && (
        <NewMeetingModal
          defaultMode={newMeetingMode}
          onCreated={handleMeetingCreated}
          onClose={() => setShowNewMeeting(false)}
          onUpgrade={() => { setShowNewMeeting(false); setPage('pricing'); }}
        />
      )}
    </div>
  );
}

function AppInner() {
  const { isLoaded, isSignedIn, user } = useUser();
  const [authMode, setAuthMode] = useState(null);

  if (!isLoaded) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ fontFamily:'var(--mono)', fontSize:13, color:'var(--text-3)' }}>Loading...</div>
    </div>
  );

  if (authMode) return <AuthScreen mode={authMode} onBack={() => setAuthMode(null)} />;

  if (!isSignedIn) return (
    <LandingPage
      onSignup={() => setAuthMode('signup')}
      onSignin={() => setAuthMode('signin')}
    />
  );

  return <Dashboard user={user} />;
}

export default function App() {
  return <AppInner />;
}
